/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { getAppHost } from '../../../utilities/TeamsUtils';

import { ConfirmationDialog, ScrollBoundary } from '../../../views/common';
import { AdaptiveCard } from '../../../views/components/AdaptiveCard';
import { ErrorPage } from '../../../views/containers/ErrorPage/ErrorPage';
import { LoadingPage } from '../../../views/containers/LoadingPage/LoadingPage';
import { ViewDetailsV2ComponentProps } from '../../../views/containers/ViewDetailsV2/ViewDetailsV2.component';
import { ViewDetailsV2Stage } from '../../../views/containers/ViewDetailsV2/ViewDetailsV2.container';
import { MobileNavigationBar } from '../../MobileNavigationBar/MobileNavigationBar';
import './MobileViewDetailsV2.scss';

export const MobileViewDetailsV2Component: React.FC<ViewDetailsV2ComponentProps> = (
    props: ViewDetailsV2ComponentProps
): JSX.Element => {
    const {
        stage,
        stageMessage,
        approvalType,
        template,
        dataPayload,
        openUrlHandler,
        submitHandler,
        confirmationDialogProps,
    } = props;

    const host = getAppHost();
    const themeName = host.getTheme();
    const history = useHistory();

    let content: JSX.Element = null;

    // Remove Menu Items from top bar
    try {
        if (host.isMobile()) {
            host.setNavBarMenu([], (): boolean => {
                return true;
            });
        }
    } catch (error) {
        // do nothing, this lets us test by catching a Context initialization error
    }

    if (stage === ViewDetailsV2Stage.DataLoading || stage === ViewDetailsV2Stage.RespondLoading) {
        content = <LoadingPage message={stageMessage} />;
    } else if (stage === ViewDetailsV2Stage.Error) {
        content = <ErrorPage message={stageMessage} />;
    } else {
        content = (
            <>
                <ScrollBoundary isTopBoundary />
                <AdaptiveCard
                    approvalType={approvalType}
                    themeName={themeName}
                    template={template}
                    dataPayload={dataPayload}
                    openUrlHandler={openUrlHandler}
                    submitHandler={submitHandler}
                />
            </>
        );
    }

    const goBack = (): void => {
        history.goBack();
    };

    return (
        <div>
            <MobileNavigationBar onSelected={goBack} />
            <div className='view-container' aria-live='polite'>
                <div className='view-details'>
                    <div>{content}</div>
                </div>
            </div>
            <ConfirmationDialog {...confirmationDialogProps} />
        </div>
    );
};
